'use client';
import { Avatar, Text, RepliedMessageBubble, SwipeableItem } from '@ui/components/atoms';
import { IMessage, TMessageAction } from '../../message.typings';
import { MessageItemDescription } from './message-item-description';
import { MessageItemFiles } from './message-item-files';
import { MessageItemReactions } from './message-item-reactions';
import { useMemo, useState, useCallback } from 'react';
import { formatMessageDate } from '../../message.constants';
import { getPersonalizedSystemMessage } from '../../message.utils';
import { MessageItemReactionPanel } from './message-item-reaction-panel';
import { MessageItemWrapper } from './message-item-wrapper';
import { IConversationRestrictionOptions } from '@ui/components/organisms/conversation';
import { getBadgeInfo } from '@ui/lib/utils';
import { Badge } from '@ui/components/organisms/badge';
import { MessageItemDropdown } from './message-item-dropdown';
import { twMerge } from 'tailwind-merge';

const defaultRestrictions: Required<IConversationRestrictionOptions> = {
  fans_react: false,
  fans_reply: false,
  fans_report: false,
  fans_can_create_posts: false,
  creator_enabled_topics: false,
  moderators_ids: [],
  moderators_profiles: [],
};

interface IProps {
  message: IMessage;
  messageDescriptionClassName?: string;
  isCreator: boolean;
  restrictions: IConversationRestrictionOptions | undefined;
  user_id: string;
  user_username?: string;
  setSelectedMessage: (message: IMessage, action: TMessageAction) => void;
  onEdit: (message: IMessage) => void;
  onReply?: (message: IMessage) => void;
  onScrollToMessage?: (messageId: string) => void;
  /** Read-only viewer (a former cohort-chat member): no edit or react affordances. */
  readOnly?: boolean;
}

export const MessageItem = ({
  message,
  messageDescriptionClassName,
  isCreator,
  restrictions,
  user_id,
  user_username,
  onEdit,
  onReply,
  onScrollToMessage,
  setSelectedMessage,
  readOnly = false,
}: IProps) => {
  // All hooks must be called before any early returns
  const isSystemMessage = message.miscellaneous?.system === true;
  const personalizedSystemMessage = useMemo(
    () => getPersonalizedSystemMessage(message, user_id),
    [message, user_id],
  );

  const formattedDate = useMemo(() => formatMessageDate(message.updated_at), [message.updated_at]);
  const [showPanel, setShowPanel] = useState(false);
  const showPanelCallback = useCallback(() => setShowPanel(true), []);
  const hidePanelCallback = useCallback(() => setShowPanel(false), []);
  const showMobilePanelCallback = useCallback(
    () => setSelectedMessage(message, 'mobile-more-menu'),
    [message, setSelectedMessage],
  );

  const managedRestrictions: IConversationRestrictionOptions = useMemo(() => {
    return {
      ...defaultRestrictions,
      ...restrictions,
    };
  }, [restrictions]);
  // Existing reactions stay visible to a read-only viewer; only adding one is hidden.
  const panelRestrictions: IConversationRestrictionOptions = useMemo(
    () => (readOnly ? { ...managedRestrictions, fans_react: false } : managedRestrictions),
    [readOnly, managedRestrictions],
  );

  const info = getBadgeInfo(message?.rich_description);
  const onEditCallback = useCallback(() => onEdit(message), [onEdit, message]);
  const onReplyCallback = useCallback(() => onReply?.(message), [onReply, message]);
  const onCopyCallback = useCallback(() => {
    if (message?.description) {
      try {
        navigator.clipboard.writeText(message.description);
      } catch {
        // noop
      }
    }
  }, [message?.description]);
  const hasAnyRestrictions = Object.values(managedRestrictions).length > 0;

  const isOwnMessage = useMemo(() => message.user_id === user_id, [message.user_id, user_id]);

  // Early return for system messages (after all hooks)
  if (isSystemMessage) {
    return (
      <div className="flex justify-center py-1">
        <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-600">
          {personalizedSystemMessage}
        </span>
      </div>
    );
  }

  return (
    <MessageItemWrapper
      gesture={'hover'}
      onShowPanel={hasAnyRestrictions ? showPanelCallback : undefined}
      onHidePanel={hasAnyRestrictions ? hidePanelCallback : undefined}>
      <div className={twMerge('flex', isOwnMessage ? 'justify-end' : '')}>
        <div className="relative">
          <div className="flex w-full justify-center">
            {info && (
              <div className="border-note mb-8 flex w-[90%] items-center justify-center border-b pb-1 backdrop-blur-lg">
                <Badge
                  type={info?.type}
                  image={info?.image}
                  size="xxs"
                  color={info?.color}
                  className="mr-4 lg:mr-2"
                  showSerialNumber={false}
                />
                <Text type="s" className="text-note">
                  {`${
                    isCreator
                      ? 'Auto message sent with '
                      : 'You received this message after collecting '
                  } ${info?.name}`}
                </Text>
              </div>
            )}
          </div>
          <div
            className={twMerge(
              'absolute z-40',
              isOwnMessage ? 'right-[5px] top-[35px]' : 'left-[10px] top-[35px]',
            )}>
            <MessageItemDropdown
              onEdit={!readOnly && message.user_id === user_id ? onEditCallback : undefined}
              onReply={
                onReply && (isCreator || managedRestrictions.fans_reply)
                  ? onReplyCallback
                  : undefined
              }
              onCopy={message?.description ? onCopyCallback : undefined}
            />
          </div>
          <div
            className={`relative flex flex-row items-center gap-1 ${
              isOwnMessage ? 'flex-row-reverse' : ''
            }`}>
            <MessageItemWrapper
              gesture={'tap'}
              onShowPanel={managedRestrictions.fans_react ? showMobilePanelCallback : undefined}>
              <SwipeableItem onSwipeAction={onReply ? onReplyCallback : undefined}>
                <div
                  className={`flex flex-row gap-1 pb-8 md:pb-0 ${
                    isOwnMessage ? 'flex-row-reverse' : ''
                  }`}>
                  <div className="flex-none shrink-0">
                    <Avatar
                      src={message.user_avatar_url}
                      displayName={message?.user_username}
                      size="s"
                    />
                  </div>
                  <div className="flex flex-col">
                    <div
                      className={`flex items-center gap-1 ${
                        isOwnMessage ? 'flex-row-reverse' : ''
                      }`}>
                      <p className="text-sm font-medium">
                        {isOwnMessage ? 'You' : message.user_username}
                      </p>
                    </div>
                    {info ? (
                      <img
                        src={message?.image_url}
                        alt={message?.name}
                        className={` ${
                          !message?.image_url && 'hidden'
                        } mb-2 h-64 rounded-lg object-cover`}
                      />
                    ) : (
                      <MessageItemFiles files={message.files} />
                    )}
                    {(message.description || message.miscellaneous?.reply_to) && (
                      <div
                        className={twMerge(
                          'relative flex flex-col bg-white rounded px-3 pt-2 pb-4 pr-9 select-text',
                          messageDescriptionClassName,
                        )}>
                        {message.miscellaneous?.reply_to && (
                          <div className="mb-2">
                            <RepliedMessageBubble
                              username={
                                message.miscellaneous.reply_to?.username === user_username
                                  ? 'You'
                                  : message.miscellaneous.reply_to?.username
                              }
                              message={message.miscellaneous.reply_to?.message}
                              isOwnMessage={isOwnMessage}
                              onPress={
                                message.miscellaneous.reply_to?.messageId && onScrollToMessage
                                  ? () =>
                                      onScrollToMessage(message.miscellaneous!.reply_to!.messageId!)
                                  : undefined
                              }
                            />
                          </div>
                        )}
                        {message.description && (
                          <div>
                            <MessageItemDescription
                              message={message}
                              className="p-0 bg-transparent max-w-full"
                            />
                          </div>
                        )}
                        <div className="absolute bottom-1 right-2 text-[10px] leading-none text-gray-400">
                          {formattedDate}
                        </div>
                      </div>
                    )}
                    {managedRestrictions.fans_react && (
                      <MessageItemReactions
                        message={message}
                        contextUserType={isCreator ? 'creator' : 'fan'}
                      />
                    )}
                  </div>
                </div>
              </SwipeableItem>
            </MessageItemWrapper>
            {showPanel && hasAnyRestrictions && (
              <MessageItemReactionPanel
                message={message}
                contextUserType={isCreator ? 'creator' : 'fan'}
                setSelectedMessage={setSelectedMessage}
                restrictions={panelRestrictions}
              />
            )}
          </div>
        </div>
      </div>
    </MessageItemWrapper>
  );
};
